import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Spin, Select, Button, DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { type AppDispatch } from '../../redux/store';
import { getReportsAction, viewReportsAction } from '../Loan/store/loanActions';
import LoanDataDisplay from './LoanDataDisplay';
import ExpenseReportDisplay from './ExpenseReportDisplay';
import InterestAccruedDisplay from './InterestAccruedDisplay';
import InterestPayableDisplay from './InterestPayableDisplay';
import DailyReportDisplay from './DailyReportDisplay';
import RefinancingReportDisplay from './RefinancingReportDisplay';
import GSTR1ReportDisplay from './GSTR1ReportDisplay';
import CashBookDisplay from './CashBookDisplay';
import CashFlowReportDisplay from './CashFlowReportDisplay';
import AverageRateTrackerDisplay from './AverageRateTrackerDisplay';
import StockReportDisplay from './StockReportDisplay';
import IncentivesTrackerDisplay from './IncentivesTrackerDisplay';
import OrganizationLoanReportDisplay from './OrganizationLoanReportDisplay';
import OrganizationLoanReleaseReportDisplay from './OrganizationLoanReleaseReportDisplay';
import ObLoanReportDisplay from './ObLoanReportDisplay';
import ObReleaseReportDisplay from './ObReleaseReportDisplay';
import PnlReportDisplay from './PnlReportDisplay';
const { RangePicker } = DatePicker;

export const ReportsIndex = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [branchOptions, setBranchOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> =
        parsedBranches.map((branch: { code: string; id: string }) => ({
          label: branch.code,
          value: branch.id,
        }));
      branchOptionTemp.push({
        label: 'All Branches',
        value: 'all',
      });
      setBranchOptions(branchOptionTemp);
    }
  }, []);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [branch, setBranch] = useState<string>('');
  // const [dateRange, setDateRange] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [typeOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([
    { label: 'Interest Accrued Report', value: 'loan' },
    { label: 'Interest Payable Report', value: 'pledge' },
    { label: 'Daily Collection Report', value: 'daily' },
    { label: 'Active Loan Report', value: 'active' },
    { label: 'Refinancing Loan os with Interest', value: 'refinance' },
    { label: 'Expenses Tracker', value: 'expense' },
    { label: 'GSTR1 Report', value: 'gstr1' },
    { label: 'Cash Bank Book', value: 'cashbook' },
    { label: 'Cash Flow Report', value: 'cashflow' },
    { label: 'Average Rate Tracker', value: 'averageRate' },
    { label: 'Stock Report', value: 'stock' },
    { label: 'Incentives Tracker', value: 'incentives' },
    { label: 'Organization Loan Report', value: 'orgLoan' },
    { label: 'Organization Loan Release Report', value: 'orgLoanRelease' },
    { label: 'OB Loan Report', value: 'obLoan' },
    { label: 'OB Release Report', value: 'obRelease' },
    { label: 'P&L Report', value: 'pnl' },
  ]);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [asOfDate, setAsOfDate] = useState<Dayjs | null>(null);

  // Incentives are only tracked for pledges started on or after the scheme go-live
  const INCENTIVES_MIN_DATE = dayjs('2026-05-01');

  const getReports = async (): Promise<void> => {
    setIsLoading(true);
    await getReportsAction(dispatch, { branch, type, startDate, endDate });
    setIsLoading(false);
  };

  const [activeLoanReport, setActiveLoanReport] = useState<any>([]);

  const viewReports = async (): Promise<void> => {
    setIsLoading(true);
    const response = await viewReportsAction(dispatch, {
      branch,
      type,
      startDate,
      endDate,
    });
    if (response != null) {
      setActiveLoanReport(response.responseData);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    // if (type !== 'active') {
    setActiveLoanReport([]);
    // }
    if (type === 'active') {
      setBranch('');
    }
  }, [type, branch, startDate, endDate]);

  const dateFormat = 'YYYY-MM-DD';

  const onChangeDate = (
    values: [Dayjs | null, Dayjs | null] | null,
    formatString: [string, string]
  ) => {
    setDateRange(values);
    setStartDate(formatString[0]);
    setEndDate(formatString[1]);
  };

  const disabledDate = (current: Dayjs) =>
    type === 'incentives' && current.isBefore(INCENTIVES_MIN_DATE, 'day');

  // Stock is a position on a single day, not a range; left empty it reports the live position
  const onChangeAsOfDate = (
    value: Dayjs | null,
    formatString: string | string[]
  ) => {
    setAsOfDate(value);
    const picked = typeof formatString === 'string' ? formatString : '';
    setStartDate(picked);
    setEndDate(picked);
  };

  const disabledAsOfDate = (current: Dayjs) => current.isAfter(dayjs(), 'day');

  return (
    <div className="loan__recovery__index__page">
      <Spin spinning={isLoading} fullscreen />
      <div className="title">
        <h1>Reports</h1>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4>Type</h4>
          <Select
            style={{ width: 311, height: 56 }}
            placeholder="Select Type"
            onChange={(selectedvalue: string): void => {
              // cleared here rather than in the effect below, so the incoming display
              // component never renders once against the previous report's rows
              setActiveLoanReport([]);
              setType(selectedvalue);
              // a range picked for another report may fall outside this one's allowed dates
              setDateRange(null);
              setAsOfDate(null);
              setStartDate('');
              setEndDate('');
            }}
            options={typeOptions}
            className="deposit__select"
          />
        </div>
        {type !== 'averageRate' && type !== 'stock' && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h4>Date</h4>
            <RangePicker
              className="rangepicker"
              value={dateRange}
              onChange={onChangeDate}
              disabledDate={disabledDate}
              format={dateFormat}
            />
          </div>
        )}
        {type === 'stock' && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h4>As of Date</h4>
            <DatePicker
              className="rangepicker"
              value={asOfDate}
              onChange={onChangeAsOfDate}
              disabledDate={disabledAsOfDate}
              format={dateFormat}
              placeholder="Today"
            />
          </div>
        )}
        {type.length > 0 && !(type === 'active') && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h4>Branch</h4>
            <Select
              style={{ width: 311, height: 56 }}
              placeholder="Select branch"
              onChange={(selectedvalue: string): void => {
                setBranch(selectedvalue);
              }}
              options={branchOptions}
              // options={[{ value: 'krr', label: 'KRR' }]}
              className="deposit__select"
            />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
          <Button
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            disabled={(type !== 'active' && branch === '') || !type}
            onClick={getReports}
            className="generate__button"
          >
            <span>Download Report</span>
          </Button>
          <Button
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            disabled={(type !== 'active' && branch === '') || !type}
            onClick={viewReports}
            className="generate__button"
          >
            <span>View Report</span>
          </Button>
        </div>
      </div>
      {type === 'active' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <LoanDataDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'expense' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <ExpenseReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'loan' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <InterestAccruedDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'pledge' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <InterestPayableDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'daily' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <DailyReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'refinance' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <RefinancingReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'gstr1' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <GSTR1ReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'cashbook' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <CashBookDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'cashflow' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <CashFlowReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'averageRate' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <AverageRateTrackerDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'stock' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <StockReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'incentives' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <IncentivesTrackerDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'orgLoan' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <OrganizationLoanReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'orgLoanRelease' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <OrganizationLoanReleaseReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'obLoan' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <ObLoanReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'obRelease' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <ObReleaseReportDisplay data={activeLoanReport} />
        </div>
      )}
      {type === 'pnl' && activeLoanReport?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <PnlReportDisplay data={activeLoanReport} />
        </div>
      )}
    </div>
  );
};
